// CSS の resize（resize-x / .resizable など）はマウス専用でタッチに反応しないため、
// それらのセル（横方向リサイズが有効な列）をタッチでも幅変更できるようにする
// グローバルハンドラ。
//
// マウス操作は従来どおり CSS resize が担当するため、ここではタッチのみを扱う。
// 個別のテーブルを編集せず document 委譲で一括対応する。
// 対象は class 名ではなく computed style の resize で判定するため、
// "resizable" クラス・"resize-x" ユーティリティのどちらでも拾える。

// リサイズ開始とみなす右端からの距離（px）
const EDGE_ZONE = 18;
// 最小幅。useTableColumnWidths の既定値と揃える
const MIN_WIDTH = 50;
// リサイズ方向を確定するための最小移動量（px）
const COMMIT_THRESHOLD = 4;

/**
 * タッチ位置から最も近いセル（th / td）を取得し、横方向の CSS resize が
 * 有効（resize: horizontal / both）ならそのセルを返す。無ければ null。
 * 列リサイズは th / td に設定されるため、セル内のネスト深さに依存せず判定できる。
 */
function findResizableElement(start) {
  const cell = start?.closest?.("th, td");
  if (!cell) return null;
  const resize = window.getComputedStyle(cell).resize;
  return resize === "horizontal" || resize === "both" ? cell : null;
}

// 多重初期化を防ぐフラグ
let initialized = false;

/**
 * タッチによるカラム幅変更を有効化する。
 * アプリ起動時に 1 回だけ呼び出す。
 */
export function enableTouchColumnResize() {
  if (initialized || typeof document === "undefined") return;
  initialized = true;

  // リサイズ中の状態。null のときは非アクティブ。
  let state = null;

  const handleTouchStart = (event) => {
    // 複数指の操作（ピンチ等）は対象外
    if (!event.touches || event.touches.length !== 1) return;
    const touch = event.touches[0];
    const target = findResizableElement(event.target);
    if (!target) return;
    const rect = target.getBoundingClientRect();
    // 右端から EDGE_ZONE 以内で開始した場合のみリサイズ候補とする
    if (touch.clientX > rect.right || rect.right - touch.clientX > EDGE_ZONE) return;
    state = {
      el: target,
      startX: touch.clientX,
      startY: touch.clientY,
      startWidth: rect.width,
      // 横移動が確定するまではスクロールを妨げない
      committed: false
    };
  };

  const handleTouchMove = (event) => {
    if (!state) return;
    const touch = event.touches && event.touches[0];
    if (!touch) return;

    const deltaX = touch.clientX - state.startX;
    const deltaY = touch.clientY - state.startY;

    if (!state.committed) {
      // 移動量が小さいうちは方向を判定しない
      if (Math.abs(deltaX) < COMMIT_THRESHOLD && Math.abs(deltaY) < COMMIT_THRESHOLD) {
        return;
      }
      // 縦方向が優勢ならスクロール意図とみなし、リサイズを中止する
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        state = null;
        return;
      }
      state.committed = true;
    }

    // スクロールを抑止してリサイズを優先する（iOS 対策で cancelable を確認）
    if (event.cancelable) {
      event.preventDefault();
    }
    event.stopPropagation();

    const newWidth = Math.max(MIN_WIDTH, Math.round(state.startWidth + deltaX));
    // style プロップ未使用の th のため、直接 DOM へ適用しても再描画で消えない
    state.el.style.width = `${newWidth}px`;
  };

  const handleTouchEnd = () => {
    state = null;
  };

  // capture は使わず bubble フェーズで登録する（DnD ポリフィルとの干渉回避）。
  // スクロール抑止のため touchmove のみ passive: false で登録する。
  document.addEventListener("touchstart", handleTouchStart, { passive: true });
  document.addEventListener("touchmove", handleTouchMove, { passive: false });
  document.addEventListener("touchend", handleTouchEnd, { passive: true });
  document.addEventListener("touchcancel", handleTouchEnd, { passive: true });
}
